import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { TrendingUp, Activity, PieChart as PieIcon, BarChart3 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#F97316', '#FB923C', '#FDBA74', '#FED7AA', '#FFEDD5', '#EA580C', '#C2410C', '#9A3412', '#7C2D12'];
const STATUS_COLORS = {
  draft: '#64748B',
  review: '#EAB308',
  published: '#22C55E',
  paused: '#F97316',
  cancelled: '#EF4444',
  finished: '#475569'
};

const AnalyticsCharts = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/organizer/analytics`, {
        withCredentials: true
      });
      setData(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="bg-white h-72 rounded-lg animate-pulse"></div>)}
      </div>
    );
  }

  if (!data) return null;

  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${months[parseInt(month) - 1]} ${year.slice(2)}`;
  };

  const rsvpsData = data.rsvps_timeline.map(d => ({ ...d, month: formatMonth(d.month) }));
  const revenueData = data.revenue_timeline.map(d => ({ ...d, month: formatMonth(d.month) }));

  return (
    <div className="space-y-6" data-testid="analytics-charts">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* RSVPs Timeline */}
        <Card className="bg-white/50 border-[#E5E5E5]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#0A0A0A] text-lg">
              <Activity className="w-5 h-5 text-primary" />
              RSVPs por mes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {rsvpsData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={rsvpsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                  <XAxis dataKey="month" stroke="#94A3B8" style={{ fontSize: '12px' }} />
                  <YAxis stroke="#94A3B8" style={{ fontSize: '12px' }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #262626', borderRadius: '8px', color: '#F8FAFC' }}
                  />
                  <Line type="monotone" dataKey="rsvps" stroke="#F97316" strokeWidth={3} dot={{ fill: '#F97316', r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-[#737373] py-12">Sin datos aún</p>
            )}
          </CardContent>
        </Card>

        {/* Revenue Timeline */}
        <Card className="bg-white/50 border-[#E5E5E5]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#0A0A0A] text-lg">
              <TrendingUp className="w-5 h-5 text-primary" />
              Ingresos por mes (ARS)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {revenueData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                  <XAxis dataKey="month" stroke="#94A3B8" style={{ fontSize: '12px' }} />
                  <YAxis stroke="#94A3B8" style={{ fontSize: '12px' }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #262626', borderRadius: '8px', color: '#F8FAFC' }}
                    formatter={(v) => `$${v.toLocaleString('es-AR')}`}
                  />
                  <Bar dataKey="revenue" fill="#F97316" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-[#737373] py-12">Sin datos aún</p>
            )}
          </CardContent>
        </Card>

        {/* Category Breakdown */}
        <Card className="bg-white/50 border-[#E5E5E5]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#0A0A0A] text-lg">
              <BarChart3 className="w-5 h-5 text-primary" />
              Eventos por categoría
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.category_breakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={data.category_breakdown} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                  <XAxis type="number" stroke="#94A3B8" style={{ fontSize: '12px' }} />
                  <YAxis type="category" dataKey="category" stroke="#94A3B8" style={{ fontSize: '12px' }} width={90} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #262626', borderRadius: '8px', color: '#F8FAFC' }}
                  />
                  <Bar dataKey="events" fill="#F97316" radius={[0, 8, 8, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-[#737373] py-12">Sin datos aún</p>
            )}
          </CardContent>
        </Card>

        {/* Status Distribution */}
        <Card className="bg-white/50 border-[#E5E5E5]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#0A0A0A] text-lg">
              <PieIcon className="w-5 h-5 text-primary" />
              Distribución por estado
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.status_distribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={data.status_distribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ status, percent }) => `${status} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={90}
                    dataKey="count"
                  >
                    {data.status_distribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.status] || COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #262626', borderRadius: '8px', color: '#F8FAFC' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-[#737373] py-12">Sin datos aún</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Events */}
      {data.top_events.length > 0 && (
        <Card className="bg-white/50 border-[#E5E5E5]">
          <CardHeader>
            <CardTitle className="text-[#0A0A0A]">Top 5 eventos por vistas</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.top_events}>
                <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                <XAxis dataKey="title" stroke="#94A3B8" style={{ fontSize: '11px' }} angle={-15} textAnchor="end" height={80} />
                <YAxis stroke="#94A3B8" style={{ fontSize: '12px' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #262626', borderRadius: '8px', color: '#F8FAFC' }}
                />
                <Legend wrapperStyle={{ color: '#94A3B8' }} />
                <Bar dataKey="views" fill="#F97316" name="Vistas" radius={[8, 8, 0, 0]} />
                <Bar dataKey="rsvps" fill="#FB923C" name="RSVPs" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AnalyticsCharts;
